import { createReducer } from '@reduxjs/toolkit'
import { castDraft } from 'immer'
import { Reducer } from 'redux'

import { ALL } from '@constants'
import { CampaignStatuses } from '@enums'
import { AuthActionTypes } from '@store/auth'
import {
  ActivateActionTypes,
  ActivateWithFeeActionTypes,
  CampaignActionTypes,
  CampaignsActionTypes,
  ConnectCampaignSocialAccountActionType,
  DisableRequireApprovalPromptActionTypes,
  DisconnectCampaignSocialAccountActionType,
  FinalizeCampaignActionTypes,
  ICampaign,
  ICampaignsState,
  PauseActionTypes,
  RequestShortlistShareLinkActionTypes,
  SaveCampaignActionTypes,
  UpdatePreferencesActionTypes,
} from '@store/campaigns'
import { addToSet, removeFromSet, returnSecondIfDeepEqual } from '@utils'
import { copyCampaign, copyCampaignError, copyCampaignSuccess } from './actions/copyCampaign'

const initialState: ICampaignsState = {
  campaignsById: {},
  errors: undefined,
  isFetching: false,
  lastSaveOutcome: null,
  isFetched: false,
  isFinalizing: false,
  isActivating: false,
  isActivatingWithFee: false,
  shortlistShareLinksById: {},
  isUpdatingPreferences: false,
  isDisablingRequireApprovalPrompt: false,
  isPausing: false,
  hasSeenPreviewTooltip: false,
  isConnectingIdentity: false,
  campaignIdsByStatus: {
    all: [],
    [CampaignStatuses.Active]: [],
    [CampaignStatuses.InReview]: [],
    [CampaignStatuses.Draft]: [],
    [CampaignStatuses.Scheduled]: [],
    [CampaignStatuses.Paused]: [],
    [CampaignStatuses.Expired]: [],
    [CampaignStatuses.Completed]: [],
  },
  stats: {
    all: 0,
    [CampaignStatuses.Active]: 0,
    [CampaignStatuses.InReview]: 0,
    [CampaignStatuses.Draft]: 0,
    [CampaignStatuses.Scheduled]: 0,
    [CampaignStatuses.Paused]: 0,
    [CampaignStatuses.Expired]: 0,
    [CampaignStatuses.Completed]: 0,
  },
  linksByStatus: {},
  copyCampaign: {},
}

// Shared by SAVE_SUCCESS/DISABLE_SUCCESS/FETCH_SUCCESS: they all persist a
// single campaign and clear the same set of in-flight flags. `campaignsById`
// is wrapped in `returnSecondIfDeepEqual` so a no-op response (server echoes
// back a deep-equal campaign) doesn't churn the reference. Callers layer on
// any action-specific fields (e.g. lastSaveOutcome) on top of this.
const withCampaignUpdate = (state: ICampaignsState, campaign: ICampaign) => ({
  ...state,
  isFetching: false,
  isSaving: false,
  isActivating: false,
  isDisablingRequireApprovalPrompt: false,
  isPausing: false,
  isUpdatingPreferences: false,
  campaignsById: returnSecondIfDeepEqual(
    {
      ...state.campaignsById,
      [campaign.id]: campaign,
    },
    state.campaignsById,
  ),
})

const reducer: Reducer<ICampaignsState> = (state = initialState, action) => {
  switch (action.type) {
    case CampaignActionTypes.FETCH_REQUEST:
    case CampaignsActionTypes.FETCH_REQUEST: {
      return {
        ...state,
        isFetching: true,
      }
    }

    case ActivateActionTypes.ACTIVATE_REQUEST: {
      return {
        ...state,
        isActivating: true,
      }
    }
    case PauseActionTypes.PAUSE_REQUEST: {
      return {
        ...state,
        isPausing: true,
      }
    }

    case DisableRequireApprovalPromptActionTypes.DISABLE_REQUEST: {
      return {
        ...state,
        isDisablingRequireApprovalPrompt: true,
      }
    }

    case ActivateWithFeeActionTypes.ACTIVATE_WITH_FEE_REQUEST: {
      return {
        ...state,
        isActivatingWithFee: true,
      }
    }

    case RequestShortlistShareLinkActionTypes.REQUEST_SHORTLIST_SHARE_LINK_REQUEST: {
      const { campaignId } = action.payload
      return {
        ...state,
        shortlistShareLinksById: {
          ...state.shortlistShareLinksById,
          [campaignId]: {
            ...state.shortlistShareLinksById[campaignId],
            isRequesting: true,
            error: undefined,
          },
        },
      }
    }

    case RequestShortlistShareLinkActionTypes.REQUEST_SHORTLIST_SHARE_LINK_SUCCESS: {
      const { campaignId, url } = action.payload
      return {
        ...state,
        shortlistShareLinksById: {
          ...state.shortlistShareLinksById,
          [campaignId]: {
            isRequesting: false,
            url,
            error: undefined,
          },
        },
      }
    }

    case RequestShortlistShareLinkActionTypes.REQUEST_SHORTLIST_SHARE_LINK_ERROR: {
      const { campaignId, error } = action.payload
      return {
        ...state,
        shortlistShareLinksById: {
          ...state.shortlistShareLinksById,
          [campaignId]: {
            ...state.shortlistShareLinksById[campaignId],
            isRequesting: false,
            error,
          },
        },
      }
    }

    case CampaignsActionTypes.FETCH_SUCCESS: {
      const {
        payload: { items, metadata, status, links },
      } = action

      const linksByStatus = { [ALL]: links?.filter((link) => link.rel === 'create_brief') }
      return {
        ...state,
        isFetching: false,
        isFetched: true,
        errors: undefined,
        campaignsById: items.reduce(
          (accum, curr) => ({
            ...accum,
            [curr.id]: curr,
          }),
          state.campaignsById,
        ),
        campaignIdsByStatus: {
          ...state.campaignIdsByStatus,
          [status]: addToSet([
            ...state.campaignIdsByStatus[status],
            ...items.map((campaign) => campaign.id),
          ]),
        },
        stats: metadata.stats,
        linksByStatus: {
          ...linksByStatus,
          ...state.linksByStatus,
          [status]: links,
        },
      }
    }

    case PauseActionTypes.PAUSE_SUCCESS:
    case ActivateActionTypes.ACTIVATE_SUCCESS: {
      const campaign = action.payload
      const affectedStatuses: ReadonlyArray<CampaignStatuses> = [
        CampaignStatuses.Active,
        CampaignStatuses.Paused,
        CampaignStatuses.Scheduled,
      ]
      const campaignIdsForAffectedStatuses = affectedStatuses.reduce((accum, curr) => {
        const campaignIdsForCurrentStatus = state.campaignIdsByStatus[curr]
        const newSet = removeFromSet(campaignIdsForCurrentStatus || [], campaign.id)
        return {
          ...accum,
          [curr]: newSet,
        }
      }, {})
      return {
        ...state,
        isFetching: false,
        isActivating: false,
        isPausing: false,
        campaignsById: {
          ...state.campaignsById,
          [campaign.id]: {
            ...state.campaignsById[campaign.id],
            ...campaign,
          },
        },
        campaignIdsByStatus: { ...state.campaignIdsByStatus, ...campaignIdsForAffectedStatuses },
      }
    }

    case SaveCampaignActionTypes.SAVE_REQUEST: {
      // Clear the previous outcome so a stale 'success'/'error' from an
      // earlier save can't be misread as this save's result.
      return { ...state, isSaving: true, lastSaveOutcome: null }
    }

    case SaveCampaignActionTypes.SAVE_SUCCESS: {
      return {
        ...withCampaignUpdate(state, action.payload),
        lastSaveOutcome: 'success',
      }
    }

    case DisableRequireApprovalPromptActionTypes.DISABLE_SUCCESS:
    case CampaignActionTypes.FETCH_SUCCESS: {
      // Deliberately does NOT touch lastSaveOutcome (BRAND-9475 review finding
      // #2): both actions share this reducer branch with SAVE_SUCCESS
      // (isSaving:false + campaignsById replacement) but neither one is the
      // outcome of a save the user triggered. If either landed mid-save it
      // must not be mistaken for that save resolving.
      return withCampaignUpdate(state, action.payload)
    }

    case FinalizeCampaignActionTypes.FINALIZE_REQUEST: {
      return {
        ...state,
        isFinalizing: true,
      }
    }

    case FinalizeCampaignActionTypes.FINALIZE_SUCCESS: {
      const campaignId: number = action.payload
      return {
        ...state,
        campaignIdsByStatus: {
          ...state.campaignIdsByStatus,
          [CampaignStatuses.Draft]: state.campaignIdsByStatus[CampaignStatuses.Draft].filter(
            (id) => id !== campaignId,
          ),
        },
        isFinalizing: false,
      }
    }

    case SaveCampaignActionTypes.SAVE_ERROR: {
      return {
        ...state,
        isFetching: false,
        isSaving: false,
        isActivating: false,
        isPausing: false,
        isUpdatingPreferences: false,
        isFinalizing: false,
        isActivatingWithFee: false,
        lastSaveOutcome: 'error',
        errors: action.payload,
      }
    }

    case UpdatePreferencesActionTypes.UPDATE_ERROR:
    case DisableRequireApprovalPromptActionTypes.DISABLE_ERROR:
    case FinalizeCampaignActionTypes.FINALIZE_ERROR:
    case PauseActionTypes.PAUSE_ERROR:
    case ActivateActionTypes.ACTIVATE_ERROR:
    case CampaignActionTypes.FETCH_ERROR:
    case CampaignsActionTypes.FETCH_ERROR:
    case ActivateWithFeeActionTypes.ACTIVATE_WITH_FEE_ERROR: {
      return {
        ...state,
        isFetching: false,
        isSaving: false,
        isActivating: false,
        isPausing: false,
        isUpdatingPreferences: false,
        isFinalizing: false,
        isActivatingWithFee: false,
        errors: action.payload,
      }
    }

    case UpdatePreferencesActionTypes.UPDATE_REQUEST: {
      return {
        ...state,
        isUpdatingPreferences: true,
      }
    }

    case AuthActionTypes.SIGNOUT_SUCCESS:
      return initialState

    case CampaignsActionTypes.UPDATE_HAS_SEEN_PREVIEW_TOOLTIP: {
      return {
        ...state,
        hasSeenPreviewTooltip: true,
      }
    }

    case ConnectCampaignSocialAccountActionType.REQUEST: {
      return {
        ...state,
        isConnectingIdentity: true,
      }
    }

    case ConnectCampaignSocialAccountActionType.SUCCESS: {
      return {
        ...state,
        isConnectingIdentity: false,
      }
    }

    case DisconnectCampaignSocialAccountActionType.REQUEST: {
      return {
        ...state,
        isConnectingIdentity: true,
      }
    }

    case DisconnectCampaignSocialAccountActionType.SUCCESS: {
      return {
        ...state,
        isConnectingIdentity: false,
      }
    }

    case DisconnectCampaignSocialAccountActionType.ERROR:
    case ConnectCampaignSocialAccountActionType.ERROR: {
      return {
        ...state,
        isConnectingIdentity: false,
        errors: action.payload,
      }
    }

    case ActivateWithFeeActionTypes.ACTIVATE_WITH_FEE_SUCCESS: {
      return {
        ...state,
        errors: undefined,
        isActivatingWithFee: false,
      }
    }

    default:
      return state
  }
}

export const campaignsReducer = createReducer(initialState, (builder) =>
  builder
    .addCase(copyCampaign, (state, { payload }) => {
      const { campaignId } = payload
      state.copyCampaign[campaignId] = state.copyCampaign[campaignId] || {}
      state.copyCampaign[campaignId].isLoading = true
      state.copyCampaign[campaignId].error = undefined
    })
    .addCase(copyCampaignSuccess, (state, { payload }) => {
      const { campaignId } = payload
      state.copyCampaign[campaignId].isLoading = false
      state.copyCampaign[campaignId].error = undefined
    })
    .addCase(copyCampaignError, (state, { payload }) => {
      const { campaignId, error } = payload
      state.copyCampaign[campaignId].isLoading = false
      state.copyCampaign[campaignId].error = castDraft(error)
    })
    .addDefaultCase(reducer),
)

export default campaignsReducer
